import { observer } from "mobx-react";
import { Icon } from "@/components-v2/Icon";
import { copyToClipboard } from "@/events/eventsDomain";
import { useEffect } from "react";
import {
  createApiKey,
  EditPasswordProps,
  hideNewApiKey,
  prepAdditionalEmailTable,
  prepApiKeyTable,
  prepEmailField,
  prepLoginMethodTable,
  prepPasswordField,
  prepPhoneNumberField,
  prepVerifyEmailButton,
  ShowPasswordProps,
  UserSettingsProps,
} from "@/userSettings/userSettingsDomain.ts";
import { EditableField } from "@/components-v2/EditableField";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout.tsx";
import { useTranslation } from "@/stores/RootStoreContext.ts";
import { Box, Card, Container, VBox } from "@/components-v2/Box.tsx";
import { Button, CloseButton, IconButton } from "@/components-v2/Button.tsx";
import { Tabs } from "@/components-v2/Tabs.tsx";
import { Alert } from "@/components-v2/Alert.tsx";
import { Markdown } from "@/text/Markdown.tsx";
import { MRadioButtonGroup } from "@/components-v2/RadioButton.tsx";
import Form from "@/components-v2/Form.tsx";
import { MTextInput } from "@/components-v2/TextInput.tsx";
import { DataTable } from "@/components-v2/DataTable.tsx";

export const UserSettingsView = observer((props: UserSettingsProps) => {
  return (
    <FullScreenLayout
      alertStore={props.messageStore}
      pageHeader={<UserSettingsPageHeader {...props} />}
      centerPanel={
        props.state.isLoading ? (
          "loader"
        ) : (
          <Container maxWidth={"md"} paddingY>
            <UserSettingsTabView {...props} />
          </Container>
        )
      }
    />
  );
});

const UserSettingsPageHeader = observer((props: UserSettingsProps) => {
  const { t } = useTranslation();
  return (
    <VBox bg borderBottom gap={0}>
      <Box alignItems={"center"} gap={"md"} paddingX={"md"} paddingY={"md"}>
        <CloseButton onClick={props.navigateBack} />
        {t("user-settings")}
      </Box>
      <Container maxWidth={"md"}>
        <VBox gap={"lg"} paddingTop={"lg"}>
          <h3>{props.name}</h3>
          <Tabs tabs={props.tabs} />
        </VBox>
      </Container>
    </VBox>
  );
});

const UserSettingsTabView = observer((props: UserSettingsProps) => {
  switch (props.userSettingsTab) {
    case "account":
      return <AccountTab {...props} />;
    case "additional-emails":
      return <AdditionalEmailsTab {...props} />;
    case "api-key":
      return <ApiKeyTab {...props} />;
  }
});

const AccountTab = observer((props: UserSettingsProps) => (
  <VBox>
    <Card title={{ key: "user-settings.contact-info" }} gap={"md"}>
      <EmailField {...props} />
      <PhoneNumberField {...props} />
    </Card>
    {props.isNorwegianAuthenticatedCountry && <PasswordAuthentication {...props} />}
    <LoginMethodsCard {...props} />
  </VBox>
));

const AdditionalEmailsTab = observer((props: UserSettingsProps) => {
  const table = prepAdditionalEmailTable(props.state);
  return (
    <VBox marginTop>
      <DataTable {...table} />
    </VBox>
  );
});

const ApiKeyTab = observer((props: UserSettingsProps) => {
  const { t } = useTranslation();
  const { state, dispatch } = props;
  const table = prepApiKeyTable(state);

  useEffect(() => () => dispatch(hideNewApiKey()), [dispatch]);

  return (
    <Card
      icon={"lock-key"}
      title={t("user-settings.api-key")}
      subTitle={state.newApiKey && <NewApiKey apiKey={state.newApiKey.key} />}
    >
      <VBox gap>
        {table.tableData.length >= 1 && <DataTable {...table} />}
        <Box marginTop>
          <Button
            variant={"secondary"}
            label={t("user-settings.api-key.create-new-key")}
            loading={state.isCreatingApiKey}
            onClick={createApiKey}
          />
        </Box>
        <Markdown value={t("user-settings.api-key.link")} enableLinks />
      </VBox>
    </Card>
  );
});

const PasswordAuthentication = observer((props: UserSettingsProps) => {
  const { t } = useTranslation();

  return (
    <Card title={t("user-settings.password-and-auth")}>
      <VBox gap>
        <div className={"label"}>{t("user-settings.username")}</div>
        <MRadioButtonGroup model={props.usernameTypeModel} horizontal />
        <PasswordField {...props} />
      </VBox>
    </Card>
  );
});

const EmailField = observer(
  ({ state, user, dispatch, refreshUser, additionalEmailInfo }: UserSettingsProps) => {
    const { t } = useTranslation();
    const button = prepVerifyEmailButton(state, user);
    return (
      <VBox>
        <EditableField {...prepEmailField(state, user, dispatch, refreshUser)} />
        <Box alignItems={"center"}>{button && <Button {...button} />}</Box>
        {additionalEmailInfo && (
          <Alert
            message={<Markdown value={t(additionalEmailInfo)} enableLinks />}
            type={"info"}
            icon={"info"}
          />
        )}
      </VBox>
    );
  },
);

const PhoneNumberField = observer(({ state, user, dispatch, refreshUser }: UserSettingsProps) => (
  <EditableField {...prepPhoneNumberField(state, user, dispatch, refreshUser)} />
));

const PasswordField = observer(({ state, dispatch, user }: UserSettingsProps) => {
  const props = prepPasswordField(state, dispatch, user);
  return props.mode === "edit" ? <EditPassword {...props} /> : <ShowPassword {...props} />;
});

const ShowPassword = observer(
  ({
    hasPassword,
    isEmailAvailable,
    hasCreatedPassword,
    isLoading,
    onEdit,
    createPassword,
  }: ShowPasswordProps) => {
    const { t } = useTranslation();
    return (
      <VBox>
        <div className={"label"}>{t("user-settings.password")}</div>
        {hasPassword ? (
          <Box alignItems={"center"}>
            <div>****************</div>
            <IconButton
              icon={"edit"}
              tooltip={t("user-settings.change-password")}
              onClick={onEdit}
              compact
            />
          </Box>
        ) : !isEmailAvailable ? (
          <Alert
            type={"info"}
            message={t("user-settings.register-email-before-creating-password")}
          />
        ) : hasCreatedPassword ? (
          <Alert type={"info"} message={t("user-settings.create-password-requested")} />
        ) : (
          <Box>
            <Button
              label={t("user-settings.create-password")}
              variant={"secondary"}
              loading={isLoading}
              size={"sm"}
              onClick={createPassword}
            />
          </Box>
        )}
      </VBox>
    );
  },
);

const EditPassword = observer(({ form, onSave, onCancel }: EditPasswordProps) => {
  const { t } = useTranslation();

  return (
    <Form onSubmit={onSave} onCancel={onCancel} loading={form.isSaving}>
      <VBox>
        <div className={"label"}>{t("user-settings.password")}</div>
        <MTextInput
          model={form.oldPassword}
          label={t("user-settings.old-password")}
          type={"password"}
          autofocus
        />
        <MTextInput
          model={form.newPassword}
          label={t("user-settings.new-password")}
          type={"password"}
        />
        <MTextInput
          model={form.confirmedPassword}
          label={t("user-settings.confirm-password")}
          type={"password"}
        />
        <Box>
          <Button type={"submit"} variant={"primary"} label={t("save")} loading={form.isSaving} />
          <Button
            variant={"secondary"}
            label={t("cancel")}
            onClick={onCancel}
            disabled={form.isSaving}
          />
        </Box>
      </VBox>
    </Form>
  );
});

const LoginMethodsCard = observer(({ state }: UserSettingsProps) => {
  const { t } = useTranslation();
  const table = prepLoginMethodTable(state);
  return (
    <Card title={{ key: "login-method" }}>
      {table.tableData.length === 0 ? (
        <Box fg={"subtle"}>{t("no-login-method-available")}</Box>
      ) : (
        <DataTable {...table} />
      )}
    </Card>
  );
});

const NewApiKey = observer(({ apiKey }: { apiKey: string }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"sm"} marginTop={"sm"}>
      <Box
        alignItems={"center"}
        gap={"sm"}
        rounded
        paddingY={"xs"}
        paddingLeft={"md"}
        paddingRight={"xs"}
        className={"bg-success border border-success"}
      >
        <Box grow fg={"default"}>
          <code className={"break-all"}>{apiKey}</code>
        </Box>
        <Icon name={"check-circle"} color={"success"} weight={"fill"} size={"md"} />
        <IconButton
          icon={"copy"}
          tooltip={{ key: "general.copy-to-clipboard" }}
          onClick={[copyToClipboard(apiKey)]}
        />
      </Box>
      <Box alignItems={"center"} gap={"xs"} fg={"success"} textSize={"sm"}>
        <Icon name={"check-circle"} weight={"fill"} />
        {t("user-settings.api-key.copy-hint")}
      </Box>
    </VBox>
  );
});
